import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FiCreditCard, FiCheck, FiLoader, FiTag, FiX } from 'react-icons/fi';
import { createCheckoutSession } from '../../api/payments';
import { useToast } from '../../context/ToastContext';
import { getPlanLimits } from '../../Config/plans';
import { validatePromoCode, PromoCodeValidation } from '../../api/promoCodes';
import { Currency, useCurrency } from '../../contexts/CurrencyContext';

type Plan = 'free' | 'standard' | 'entreprise';
type BillingCycle = 'monthly' | 'yearly';

interface StripeCheckoutProps {
  currentPlan: Plan;
  suggestedPlan?: Plan;
  currency?: Currency;
  onCancel?: () => void;
}

const StripeCheckout: React.FC<StripeCheckoutProps> = ({
  currentPlan,
  suggestedPlan,
  currency: propCurrency,
  onCancel,
}) => {
  const { t } = useTranslation('settings');
  const toast = useToast();
  const navigate = useNavigate();
  const { currency: contextCurrency, formatAmount } = useCurrency();
  const currency = propCurrency || contextCurrency;
  const [selectedPlan, setSelectedPlan] = useState<Plan>(suggestedPlan || 'standard');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [loading, setLoading] = useState(false);

  // États pour le code promo
  const [promoCode, setPromoCode] = useState('');
  const [promoValidation, setPromoValidation] = useState<PromoCodeValidation | null>(null);
  const [validatingPromo, setValidatingPromo] = useState(false);

  const plans: Plan[] = ['free', 'standard', 'entreprise'];

  // Revalider automatiquement le code promo quand le cycle de facturation ou le plan change
  useEffect(() => {
    const revalidatePromoCode = async () => {
      if (promoValidation && promoCode) {
        setValidatingPromo(true);
        try {
          const amount = getPlanPrice(selectedPlan, billingCycle);
          const validation = await validatePromoCode(promoCode.trim(), selectedPlan, amount);

          if (validation.valid) {
            setPromoValidation(validation);
          } else {
            // Si le code n'est plus valide pour ce plan/cycle, le retirer
            setPromoValidation(null);
            toast.info(`Le code promo ${promoCode} n'est pas applicable à cette configuration`);
          }
        } catch (error) {
          console.error('Promo code revalidation error:', error);
        } finally {
          setValidatingPromo(false);
        }
      }
    };

    revalidatePromoCode();
  }, [billingCycle, selectedPlan]); // Déclencher quand billingCycle ou selectedPlan change

  const handleCheckout = async () => {
    if (selectedPlan === currentPlan) {
      toast.warning(t('subscription.checkout.errors.samePlan'));
      return;
    }

    if (selectedPlan === 'free') {
      toast.warning(t('subscription.checkout.errors.cannotDowngradeToFree'));
      return;
    }

    setLoading(true);

    try {
      const successUrl = `${window.location.origin}/settings/subscription?session_id={CHECKOUT_SESSION_ID}&success=true`;
      const cancelUrl = `${window.location.origin}/settings/subscription?canceled=true`;

      const response = await createCheckoutSession(
        selectedPlan,
        billingCycle,
        successUrl,
        cancelUrl,
        promoValidation?.promo_code?.code, // Pass validated promo code
        currency // Pass selected currency
      );

      // Si c'est un update d'abonnement existant (pas de redirection Stripe)
      if (response.success) {
        // Vérifier s'il y a des prévisions de dépassement de quotas
        if (response.quota_prediction?.will_exceed_quotas) {
          const periodEnd = response.subscription?.current_period_end;
          const effectiveDate = periodEnd ?
            new Date(periodEnd * 1000).toLocaleDateString('fr-FR', {year: 'numeric', month: 'long', day: 'numeric'}) :
            'la fin de votre période de facturation actuelle';

          const overagesText = response.quota_prediction.overages
            .map((o: any) => `\n• ${o.message}`)
            .join('');

          toast.success(
            `Votre changement d'abonnement prendra effet le ${effectiveDate}.${overagesText}\n\n⚠️ Vous dépasserez les limites du plan ${selectedPlan.toUpperCase()}. La création sera bloquée jusqu'à ce que vous reveniez sous les quotas.`,
            8000
          );
        } else {
          toast.success('Abonnement modifié avec succès!');
        }
        // Fermer le modal
        if (onCancel) onCancel();
        // Rafraîchir la page pour afficher le nouveau plan
        setTimeout(() => window.location.reload(), 1500);
      } else if (response.url) {
        // Sinon, rediriger vers Stripe Checkout (nouveau paiement)
        window.location.href = response.url;
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
          t('subscription.checkout.errors.generic')
      );
      setLoading(false);
    }
  };

  const getPlanPrice = (plan: Plan, cycle: BillingCycle) => {
    const limits = getPlanLimits(plan);
    if (cycle === 'monthly') {
      return limits.price_monthly_cents / 100;
    }
    return limits.price_yearly_cents / 100;
  };

  const getYearlySavings = (plan: Plan) => {
    const limits = getPlanLimits(plan);
    const monthlyTotal = (limits.price_monthly_cents * 12) / 100;
    const yearlyTotal = limits.price_yearly_cents / 100;
    const savings = monthlyTotal - yearlyTotal;
    const percentage = Math.round((savings / monthlyTotal) * 100);
    return { savings, percentage };
  };

  // Valider le code promo
  const handleValidatePromoCode = async () => {
    if (!promoCode.trim()) {
      toast.warning('Veuillez saisir un code promo');
      return;
    }

    setValidatingPromo(true);

    try {
      const amount = getPlanPrice(selectedPlan, billingCycle);
      const validation = await validatePromoCode(promoCode.trim(), selectedPlan, amount);

      if (validation.valid) {
        setPromoValidation(validation);
        toast.success(`Code promo appliqué! Réduction de ${formatAmount(validation.discount?.amount || 0, currency)}`);
      } else {
        setPromoValidation(null);
        toast.error(validation.error || 'Code promo invalide');
      }
    } catch (error: any) {
      console.error('Promo code validation error:', error);
      toast.error('Erreur lors de la validation du code promo');
    } finally {
      setValidatingPromo(false);
    }
  };

  // Supprimer le code promo
  const handleRemovePromoCode = () => {
    setPromoCode('');
    setPromoValidation(null);
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* En-tête */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          {t('subscription.checkout.title')}
        </h2>
        <p className="text-gray-600">
          {t('subscription.checkout.subtitle')}
        </p>
      </div>

      {/* Toggle billing cycle */}
      <div className="flex justify-center mb-8">
        <div className="bg-gray-100 rounded-lg p-1 inline-flex">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2 rounded-md font-medium transition ${
              billingCycle === 'monthly'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('subscription.checkout.billingCycle.monthly')}
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-6 py-2 rounded-md font-medium transition relative ${
              billingCycle === 'yearly'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('subscription.checkout.billingCycle.yearly')}
            <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
              -20%
            </span>
          </button>
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {plans.map((plan) => {
          const limits = getPlanLimits(plan);
          const price = getPlanPrice(plan, billingCycle);
          const savings = billingCycle === 'yearly' ? getYearlySavings(plan) : null;
          const isCurrent = plan === currentPlan;
          const isSelected = plan === selectedPlan;
          const isPaidPlan = plan !== 'free';

          return (
            <div
              key={plan}
              className={`relative bg-white rounded-xl shadow-sm border-2 p-6 cursor-pointer transition hover:shadow-lg ${
                isSelected
                  ? 'border-blue-600 ring-2 ring-blue-600 ring-opacity-50'
                  : 'border-gray-200'
              } ${isCurrent ? 'opacity-75' : ''}`}
              onClick={() => !isCurrent && setSelectedPlan(plan)}
            >
              {/* Current plan badge */}
              {isCurrent && (
                <div className="absolute top-4 right-4">
                  <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                    {t('subscription.checkout.currentPlan')}
                  </span>
                </div>
              )}

              {/* Recommended badge */}
              {plan === 'standard' && !isCurrent && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-bold px-4 py-1 rounded-full shadow-md">
                    {t('subscription.checkout.recommended')}
                  </span>
                </div>
              )}

              {/* Plan name */}
              <h3 className="text-2xl font-bold text-gray-900 mb-2 capitalize">
                {t(`subscription.plans.${plan}.name`)}
              </h3>

              {/* Price */}
              <div className="mb-6">
                {isPaidPlan ? (
                  <>
                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold text-gray-900">
                        {formatAmount(price, currency)}
                      </span>
                      <span className="text-gray-600 ml-2">
                        /{billingCycle === 'monthly' ? t('subscription.checkout.perMonth') : t('subscription.checkout.perYear')}
                      </span>
                    </div>
                    {savings && (
                      <p className="text-sm text-green-600 mt-1">
                        {t('subscription.checkout.saveAmount', { amount: formatAmount(savings.savings, currency), percentage: savings.percentage })}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="text-4xl font-bold text-gray-900">
                    {t('subscription.checkout.free')}
                  </div>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <FiCheck className="text-green-500 mr-2 mt-0.5 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">
                    {limits.projects === -1
                      ? t('subscription.features.unlimitedProjects')
                      : t('subscription.features.projects', { count: limits.projects })}
                  </span>
                </li>
                <li className="flex items-start">
                  <FiCheck className="text-green-500 mr-2 mt-0.5 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">
                    {limits.architectures === -1
                      ? t('subscription.features.unlimitedArchitectures')
                      : t('subscription.features.architectures', { count: limits.architectures })}
                  </span>
                </li>
                <li className="flex items-start">
                  <FiCheck className="text-green-500 mr-2 mt-0.5 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">
                    {limits.deployments_per_month === -1
                      ? t('subscription.features.unlimitedDeployments')
                      : t('subscription.features.deployments', { count: limits.deployments_per_month })}
                  </span>
                </li>
                {limits.features.azure_devops_auto_provision && (
                  <li className="flex items-start">
                    <FiCheck className="text-green-500 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">
                      {t('subscription.features.azureDevOps')}
                    </span>
                  </li>
                )}
                {limits.features.priority_support && (
                  <li className="flex items-start">
                    <FiCheck className="text-green-500 mr-2 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-sm text-gray-700">
                      {t('subscription.features.prioritySupport')}
                    </span>
                  </li>
                )}
              </ul>

              {/* Selection indicator */}
              {isSelected && !isCurrent && (
                <div className="absolute bottom-4 right-4">
                  <FiCheck className="text-blue-600" size={24} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Promo Code Section */}
      <div className="max-w-md mx-auto mb-8">
        {!promoValidation ? (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiTag className="inline mr-2" />
              Code promo (optionnel)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                onKeyPress={(e) => e.key === 'Enter' && handleValidatePromoCode()}
                placeholder="Entrez votre code"
                disabled={validatingPromo || selectedPlan === 'free'}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-900 placeholder-gray-400"
              />
              <button
                onClick={handleValidatePromoCode}
                disabled={validatingPromo || !promoCode.trim() || selectedPlan === 'free'}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {validatingPromo ? (
                  <>
                    <FiLoader className="animate-spin" size={18} />
                    Validation...
                  </>
                ) : (
                  'Appliquer'
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-green-50 rounded-lg p-4 border-2 border-green-500">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <FiCheck className="text-green-600" size={20} />
                  <span className="font-semibold text-green-900">
                    Code promo appliqué: {promoValidation.promo_code?.code}
                  </span>
                </div>
                {promoValidation.promo_code?.description && (
                  <p className="text-sm text-green-800 mb-3">
                    {promoValidation.promo_code.description}
                  </p>
                )}
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-gray-700">
                    <span>Prix initial:</span>
                    <span className="line-through">
                      {formatAmount(promoValidation.discount?.original_amount || 0, currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-green-700 font-semibold">
                    <span>Réduction:</span>
                    <span>
                      -{promoValidation.promo_code?.discount_type === 'percentage'
                        ? `${promoValidation.promo_code.discount_value}%`
                        : formatAmount(promoValidation.promo_code?.discount_value || 0, currency)}
                      {' '}(-{formatAmount(promoValidation.discount?.amount || 0, currency)})
                    </span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-green-900 pt-2 border-t border-green-200">
                    <span>Prix final:</span>
                    <span>{formatAmount(promoValidation.discount?.final_amount || 0, currency)}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={handleRemovePromoCode}
                className="text-green-700 hover:text-green-900 ml-4 flex-shrink-0"
                aria-label="Retirer le code promo"
              >
                <FiX size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex justify-center gap-4">
        {onCancel && (
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-8 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('subscription.checkout.buttons.cancel')}
          </button>
        )}
        <button
          onClick={handleCheckout}
          disabled={loading || selectedPlan === currentPlan || selectedPlan === 'free'}
          className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loading ? (
            <>
              <FiLoader className="animate-spin" size={20} />
              {t('subscription.checkout.buttons.processing')}
            </>
          ) : (
            <>
              <FiCreditCard size={20} />
              {t('subscription.checkout.buttons.proceed')}
            </>
          )}
        </button>
      </div>

      {/* Security notice */}
      <div className="text-center mt-8">
        <p className="text-sm text-gray-500">
          {t('subscription.checkout.securityNotice')}
        </p>
      </div>
    </div>
  );
};

export default StripeCheckout;
